import type { AdminLocale } from '../../content/locale.js';
import { ApiError } from '../../lib/http.js';

/* Mensagens para o fluxo de releases (ADR-0659/0670/0697). O `releaseError` (releases-api.ts)
   preserva o código específico do backend em `ApiError.message` e o HTTP status em
   `ApiError.status`. `releaseSpecificMessage` cobre os códigos de negócio da descoberta/registro;
   `releaseErrorMessage` mapeia o específico primeiro e cai no genérico por status.

   `locale` é OPCIONAL (default pt-BR) e não um `Bilingual<T>`/`useBilingual`: as duas funções
   exportadas daqui só têm consumidor em `routes/releases-page.tsx` e
   `routes/release-register-page.tsx`, frentes próprias que ainda chamam com um único argumento.
   Mesma razão de `pack-catalog-labels.ts` — o parâmetro opcional mantém compatibilidade. */

const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const HTTP_CONFLICT = 409;

interface ReleaseErrorStrings {
  readonly versionMismatch: string;
  readonly noArtifacts: string;
  readonly invalidArtifactInventory: string;
  readonly bucketUnconfigured: string;
  readonly releaseIsPromoted: string;
  readonly releaseVersionIsPromoted: string;
  readonly purgeConfirmationMismatch: string;
  readonly storageUnavailable: string;
  readonly r2DeleteFailed: string;
  readonly purgeIncomplete: string;
  readonly releaseNotFound: string;
  readonly itemFallback: string;
  readonly networkFailure: string;
  readonly forbidden: string;
  readonly conflict: string;
  readonly genericFailure: string;
  readonly unexpected: string;
}

const RELEASE_ERROR_STRINGS: Readonly<Record<AdminLocale, ReleaseErrorStrings>> = {
  'pt-BR': {
    versionMismatch:
      'Os arquivos no bucket pertencem a outra versão (o build diverge da versão informada).',
    noArtifacts: 'Nenhum artefato encontrado no bucket para essa versão.',
    invalidArtifactInventory:
      'O inventário da release não corresponde aos arquivos do bucket. Publique novamente antes de registrar.',
    bucketUnconfigured: 'O bucket de releases não está configurado neste ambiente.',
    releaseIsPromoted:
      'Esta versão está no ar — tire do ar antes de remover o registro. Desindexar a versão ativa quebra a atualização automática.',
    releaseVersionIsPromoted:
      'Ainda há artefatos desta versão no ar. Tire todos do ar e tente excluir do R2 novamente.',
    purgeConfirmationMismatch:
      'A confirmação não corresponde a canal/versão. Reabra a ação e digite exatamente o identificador exibido.',
    storageUnavailable:
      'O armazenamento desta versão foi excluído ou está em exclusão. Ela não pode ser promovida nem registrada novamente; publique uma nova versão.',
    r2DeleteFailed:
      'A exclusão no R2 falhou. O estado pendente foi preservado; use “Continuar exclusão do R2” para tentar novamente.',
    purgeIncomplete:
      'Ainda restam objetos desta versão no R2. O estado pendente foi preservado; use “Continuar exclusão do R2” novamente.',
    releaseNotFound: 'Essa release não existe mais (alguém pode ter removido o registro).',
    itemFallback: 'Falha ao aplicar a ação nesta versão.',
    networkFailure: 'Falha de conexão. Tente novamente.',
    forbidden: 'Você não tem permissão para esta ação (ou sua sessão expirou).',
    conflict: 'Conflito: a release já existe ou está num estado incompatível.',
    genericFailure: 'Não foi possível concluir a operação. Tente novamente.',
    unexpected: 'Erro inesperado.',
  },
  'en-US': {
    versionMismatch:
      'The files in the bucket belong to another version (the build diverges from the informed version).',
    noArtifacts: 'No artifact found in the bucket for this version.',
    invalidArtifactInventory:
      'The release inventory does not match the bucket objects. Publish it again before registering.',
    bucketUnconfigured: 'The releases bucket is not configured in this environment.',
    releaseIsPromoted:
      'This version is live — take it down before removing the record. Removing the active version from the index breaks auto-update.',
    releaseVersionIsPromoted:
      'Some artifacts from this version are still live. Take all of them down and retry the R2 deletion.',
    purgeConfirmationMismatch:
      'The confirmation does not match channel/version. Reopen the action and type the exact identifier shown.',
    storageUnavailable:
      'This version’s storage was deleted or is being deleted. It cannot be promoted or registered again; publish a new version.',
    r2DeleteFailed:
      'The R2 deletion failed. The pending state was preserved; use “Continue R2 deletion” to retry.',
    purgeIncomplete:
      'Some objects from this version remain in R2. The pending state was preserved; use “Continue R2 deletion” again.',
    releaseNotFound: 'This release no longer exists (someone may have removed the record).',
    itemFallback: 'Failed to apply the action to this version.',
    networkFailure: 'Connection failure. Try again.',
    forbidden: 'You do not have permission for this action (or your session expired).',
    conflict: 'Conflict: the release already exists or is in an incompatible state.',
    genericFailure: 'Could not complete the operation. Try again.',
    unexpected: 'Unexpected error.',
  },
};

function releaseSpecificMessage(code: string, strings: ReleaseErrorStrings): string | null {
  switch (code) {
    case 'version_mismatch':
      return strings.versionMismatch;
    case 'no_artifacts':
      return strings.noArtifacts;
    case 'invalid_artifact_inventory':
      return strings.invalidArtifactInventory;
    case 'releases_bucket_unconfigured':
      return strings.bucketUnconfigured;
    case 'release_is_promoted':
      return strings.releaseIsPromoted;
    case 'release_version_is_promoted':
      return strings.releaseVersionIsPromoted;
    case 'release_purge_confirmation_mismatch':
      return strings.purgeConfirmationMismatch;
    case 'release_storage_unavailable':
      return strings.storageUnavailable;
    case 'release_r2_delete_failed':
      return strings.r2DeleteFailed;
    case 'release_purge_incomplete':
      return strings.purgeIncomplete;
    case 'release_not_found':
      return strings.releaseNotFound;
    default:
      return null;
  }
}

/** Rótulo de um código de falha POR ITEM de lote — o backend devolve código, nunca texto. */
export function releaseItemErrorMessage(
  code: string | null,
  locale: AdminLocale = 'pt-BR',
): string {
  const strings = RELEASE_ERROR_STRINGS[locale];
  return releaseSpecificMessage(code ?? '', strings) ?? strings.itemFallback;
}

export function releaseErrorMessage(err: unknown, locale: AdminLocale = 'pt-BR'): string {
  const strings = RELEASE_ERROR_STRINGS[locale];
  if (err instanceof ApiError) {
    const specific = releaseSpecificMessage(err.message, strings);
    if (specific !== null) return specific;
    if (err.code === 'network' || err.status === 0) {
      return strings.networkFailure;
    }
    if (err.status === HTTP_UNAUTHORIZED || err.status === HTTP_FORBIDDEN) {
      return strings.forbidden;
    }
    if (err.status === HTTP_CONFLICT) {
      return strings.conflict;
    }
    return strings.genericFailure;
  }
  return strings.unexpected;
}
